'use client';

import { useRouter } from '@bprogress/next/app';
import { useSelector } from '@xstate/store/react';
import { useRef } from 'react';

import {
  GTMItemListName,
  GTMViewItemList,
  GTMEcommerceItem,
  sendGTMEventEcommerce
} from '@/fo/api/gtm';
import {
  selectCountProductInCart,
  storeProduct,
  cartActions,
  wishlistActions,
  selectWishlistItemId,
  selectCartItemId,
  selectCartItemLoading
} from '@/fo/store/product';
import {
  UiProductTileSecondary,
  UiProductTileSecondaryProps
} from '@/fo/ui/product/tile/secondary';
import { utilEventAuthModal } from '@/fo/util/event-auth-modal';
import { MappedProduct } from '@/fo/util/map-product-data';

const ETAT_PUR_BRAND_NAME = 'Etat Pur';

export interface FeatureProductTileSecondaryProps {
  isAuthorized: boolean;
  data: MappedProduct;
  type: UiProductTileSecondaryProps['type'];
  showOldPrice?: boolean;
  className?: string;
  classNameActions?: string;
  isCart?: boolean;
  hideCartButton?: boolean;
  showSeriesLabel?: boolean;
  positionIndex?: number;
  viewItemListName?: GTMItemListName;
  isDiagnostic?: boolean;
  openInNewTab?: boolean;
  hideLink?: boolean;
}

export function FeatureProductTileSecondary({
  isAuthorized,
  data,
  type,
  showOldPrice = false,
  className,
  classNameActions,
  isCart,
  showSeriesLabel = false,
  positionIndex = 0,
  viewItemListName,
  isDiagnostic = false,
  openInNewTab,
  hideLink = false
}: FeatureProductTileSecondaryProps) {
  const ref = useRef<HTMLDivElement>(null);
  const router = useRouter();

  const wishlistSku = data.parentSku || data?.sku;
  const basePrice = data.basePrice || 0;
  const price = showOldPrice ? (data.finalPrice ?? basePrice) : basePrice;
  const oldPrice = showOldPrice
    ? data.finalPrice
      ? data.basePrice
      : null
    : null;

  const cartItemId = useSelector(storeProduct, selectCartItemId(data?.sku));
  const cartCount = useSelector(
    storeProduct,
    selectCountProductInCart(data?.sku)
  );
  const cartLoading = useSelector(
    storeProduct,
    selectCartItemLoading(data?.sku)
  );
  const wishlistItemId = useSelector(
    storeProduct,
    selectWishlistItemId(wishlistSku)
  );

  const isInWishlist = Boolean(wishlistItemId);

  const brand = data.labelsBrand[0];
  const series = data.labelsBrand[1];

  const itemGtmData: GTMEcommerceItem = {
    item_id: data?.sku,
    item_name: data.name,
    price: String(data.itemPrice ?? price),
    item_brand: data.brand ?? '',
    item_category: data.series || data.brand || '',
    variant: data.volume ?? '',
    position: positionIndex + 1,
    item_list_name: viewItemListName
  };

  const onClick = () => {
    sendGTMEventEcommerce({
      event: 'select_item',
      ecommerce: {
        items: [itemGtmData]
      }
    });
  };

  const onCartClick = () => {
    cartActions.addProductsToCart([
      {
        sku: data.sku,
        quantity: 1,
        product: data
      }
    ]);
  };

  const onCartChange = async (quantity: number) => {
    if (quantity === 0) {
      await cartActions.removeCartItem(data.sku, cartItemId, data);
    } else {
      await cartActions.updateCartItem(data.sku, cartItemId, quantity, data);
    }
    router.refresh();
  };

  const onWishlistClick = async (
    sku: string,
    wishlistSku: string,
    parentSku?: string,
    wishlistItemId?: string,
    isInWishlist?: boolean
  ) => {
    if (isAuthorized) {
      if (isInWishlist && wishlistItemId) {
        await wishlistActions.removeWishlistItems([
          { sku: wishlistSku, wishlistItemId }
        ]);
      } else {
        sendGTMEventEcommerce({
          event: 'add_to_wishlist',
          ecommerce: {
            value: itemGtmData.price,
            items: [{ ...itemGtmData, quantity: 1 }]
          }
        });
        await wishlistActions.addProductToWishlist(sku, 1, parentSku);
      }
    } else {
      utilEventAuthModal.open('wishlist-button');
    }
  };

  const onRemoveClick = async () => {
    await cartActions.removeCartItem(data.sku, cartItemId, data);
    router.refresh();
  };

  const getLink = () => {
    const isGiftOrSample =
      ['gift', 'sample'].includes(data?.productGroup?.name ?? '') ||
      type === 'gift' ||
      type === 'sample';

    if (isGiftOrSample && data?.catalogBrandUrl) {
      return data.catalogBrandUrl[0] === '/'
        ? data.catalogBrandUrl
        : `/${data.catalogBrandUrl}`;
    }
    return data.link;
  };

  const maxCount =
    data?.groupType && data?.items?.length
      ? Math.min(...data.items.map((item) => item.stockQuantity || 0))
      : data.stockQuantity;

  //Если бренд - Etat Pur, то не нужно отображать серию
  const isEtatPur = brand?.name === ETAT_PUR_BRAND_NAME;
  const hasGroupItems = Boolean(data?.groupType && data?.items?.length);

  return (
    <>
      {viewItemListName && (
        <GTMViewItemList
          key={`gtm-${data.sku}`}
          refItem={ref}
          item={itemGtmData}
        />
      )}
      <UiProductTileSecondary
        ref={ref}
        key={`ui-${data?.sku}`}
        className={className}
        classNameActions={classNameActions}
        isAuthorized={isAuthorized}
        type={type}
        bonuses={data.bonusPoints}
        bonusesMax={data.bonusPointsMax}
        link={
          !hideLink && !hasGroupItems && basePrice > 0 ? getLink() : undefined
        }
        openInNewTab={openInNewTab}
        image={data.thumbnail?.src || ''}
        series={showSeriesLabel || isEtatPur ? undefined : data.series}
        title={data.name}
        description={data.subtitle}
        price={price}
        oldPrice={oldPrice}
        salePrice={data.discountTotal || 0}
        brand={
          showSeriesLabel && series
            ? {
                color: series.color,
                name: series.name,
                url: series.url
              }
            : brand
              ? {
                  color: brand.color,
                  name: brand.name,
                  url: brand.url
                }
              : undefined
        }
        count={
          type === 'order' && data.quantity ? data.quantity || 0 : cartCount
        }
        maxCount={maxCount}
        isInWishlist={isInWishlist}
        options={data.configurableVariants}
        onClick={onClick}
        onCartClick={isCart ? undefined : onCartClick}
        onCartChange={onCartChange}
        onWishlistClick={() => {
          onWishlistClick(
            data.sku,
            wishlistSku,
            data.parentSku,
            wishlistItemId,
            isInWishlist
          );
        }}
        onRemoveClick={onRemoveClick}
        groupType={data?.groupType}
        childrenItems={
          data?.groupType && data?.items?.length ? data.items : undefined
        }
        onWishlistChildrenClick={onWishlistClick}
        isCart={isCart}
        isCartLoading={cartLoading}
        mindboxId={data.mindboxId}
        visibility={data.visibility}
        diagnosticLabels={data.diagnosticLabels}
        isDiagnostic={isDiagnostic}
      />
    </>
  );
}
